/* Going into a product from the hub (18 Oct): Discover › Curation, Engage › Advertising. "Grid unfold".

   The card you chose breaks into the nine tiles of the ONAM mark. They spread out from the card to cover the area
   under the header, one after another in the order the loader steps (Azure first, then round the grid, the centre
   last), each growing into a third of the screen. Under that cover the product's page opens; then the tiles fold
   away in the same order and the page is there. About 1.4 s in all, and the page loads during it. Only transform
   and opacity move (plus the tiles' corners). A click is ignored while it runs. Skipped with reduced motion. */

import { reducedMotion } from "../mark";

/** The loader's order round the 3×3 grid: top right (Azure), down the right side, along the bottom, up the left,
    across the top, the centre last. Indexes read left to right, top to bottom. */
const ORDER = [2, 5, 8, 7, 6, 3, 0, 1, 4];
const AZURE = 2;
const EASE = "cubic-bezier(.65,0,.25,1)";
const OUT = { delay: 80, step: 45, dur: 640 }; // spreading out
const BACK = { step: 40, dur: 420 }; // folding away

let running = false;

export function openFromCard(card: HTMLElement | null, go: () => void) {
  if (!card || reducedMotion() || typeof document === "undefined" || !(card as any).animate) { go(); return; }
  if (running) return;
  running = true;

  const r = card.getBoundingClientRect();
  const head = document.querySelector(".onam-head") as HTMLElement | null;
  const top = head ? head.getBoundingClientRect().bottom : 64;
  const view = { left: 0, top, width: window.innerWidth, height: window.innerHeight - top };
  const cw = view.width / 3, ch = view.height / 3;
  // the mark as it first sits in the card: a 3×3 of small tiles centred on it
  const cs = Math.min(r.width, r.height) / 3.4, gap = cs * 0.18;
  const ox = r.left + r.width / 2 - (cs * 1.5 + gap), oy = r.top + r.height / 2 - (cs * 1.5 + gap);

  const layer = document.createElement("div");
  layer.className = "onam-portal-grid";
  layer.setAttribute("aria-hidden", "true");
  document.body.appendChild(layer);

  const tiles = ORDER.map((idx, n) => {
    const gx = idx % 3, gy = Math.floor(idx / 3);
    const el = document.createElement("i");
    if (idx === AZURE) el.className = "a";
    // a hairline of overlap so no seam shows between neighbours when they cover the screen
    Object.assign(el.style, { left: `${view.left + gx * cw}px`, top: `${view.top + gy * ch}px`, width: `${cw + 1}px`, height: `${ch + 1}px` });
    layer.appendChild(el);
    const sx = ox + gx * (cs + gap), sy = oy + gy * (cs + gap);
    const from = `translate(${sx - (view.left + gx * cw)}px, ${sy - (view.top + gy * ch)}px) scale(${cs / cw}, ${cs / ch})`;
    return { el, n, from };
  });

  const anims: Animation[] = [];
  anims.push(card.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: "forwards" }));
  tiles.forEach(({ el, n, from }) => anims.push(el.animate(
    [{ transform: from, borderRadius: "22%", opacity: 0 }, { opacity: 1, offset: 0.25 }, { transform: "none", borderRadius: "0px", opacity: 1 }],
    { duration: OUT.dur, delay: OUT.delay + n * OUT.step, easing: EASE, fill: "both" })));

  let opened = false;
  const cleanup = () => { layer.remove(); anims.forEach((a) => { try { a.cancel(); } catch { /* gone */ } }); running = false; };
  const open = () => {
    if (opened) return; opened = true;
    go();
    // two frames for the new page to paint underneath, then the tiles fold away in the same order
    requestAnimationFrame(() => requestAnimationFrame(() => {
      tiles.forEach(({ el, n }) => el.animate([{ transform: "none", opacity: 1 }, { transform: "scale(.6)", opacity: 0 }],
        { duration: BACK.dur, delay: 60 + n * BACK.step, easing: EASE, fill: "forwards" }));
      window.setTimeout(cleanup, 60 + 8 * BACK.step + BACK.dur + 40);
    }));
  };
  const covered = OUT.delay + 8 * OUT.step + OUT.dur;
  window.setTimeout(open, covered);
  window.setTimeout(() => { if (layer.isConnected) cleanup(); }, covered + 2000); // never left on screen
}
